import React, { useEffect, useState, useRef } from 'react';
import axiosClient from '../api/axiosClient'; // Importación actualizada
import { useParams, useNavigate } from 'react-router-dom';
import EventForm from '../components/EventForm';
import Button from '../components/Button/Button';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import SpinnerCircular from './SpinnerCircular';
import { theme } from '../theme';
import Typography from './Typography';
import { useTranslation } from 'react-i18next';
import { isEventPast } from '../utils/dateUtils';
import DateChangeConfirmationModal from './Modal/DateChangeConfirmationModal';
import { TICKET_PAID } from '../constants/eventTypes';

const EditEvent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [initialData, setInitialData] = useState(null);
  const eventFormRef = useRef();
  const { t } = useTranslation('createEvent');
  const [showDateChangeModal, setShowDateChangeModal] = useState(false);
  const [pendingFormData, setPendingFormData] = useState(null);
  const [dateChangeInfo, setDateChangeInfo] = useState({ newDate: '', newTime: '', newEndDate: '', newEndTime: '', attendeesCount: 0, hasPaidTickets: false });


  useEffect(() => {
    const fetchEventDetails = async () => {
      try {
        // Usar el endpoint protegido para edición de eventos
        const response = await axiosClient.get(`/api/events/manage/${id}`);
        if (response.data.event) {
          const eventData = response.data.event;

          // Verificar si el evento ya ha pasado
          if (isEventPast(eventData.endDate)) {
            toast.error('No puedes editar un evento que ya ha finalizado.');
            navigate(`/events/manage/${id}`);
            return;
          }

          setInitialData(eventData);
          if (eventFormRef.current) {
            eventFormRef.current.setInitialData(eventData);
          }
        }
      } catch (error) {
        console.error('Error fetching event details:', error);
        if (error.response?.status === 403) {
          toast.error('No tienes permisos para editar este evento.');
          navigate('/events'); // Redirigir si no tiene permisos
        } else if (error.response?.status === 404) {
          toast.error('Evento no encontrado.');
          navigate('/events');
        } else {
          toast.error(t('editEvent.errorFetching', 'Error al obtener los detalles del evento.'));
        }
      }
    };
    fetchEventDetails();
  }, [id, t, navigate]);


  const handleDiscard = () => {
    navigate(`/events/manage/${id}`);
  };

  const handleSaveChanges = async (formData) => {
    // Always get formData from the form ref to ensure we have a FormData object
    if (eventFormRef.current) {
      formData = await eventFormRef.current.getFormData();
    } else {
      toast.error(t('editEvent.error', 'Hubo un error al actualizar el evento. Inténtalo de nuevo.'));
      return;
    }

    if (!formData) {
      toast.error(t('editEvent.error', 'Hubo un error al actualizar el evento. Inténtalo de nuevo.'));
      return;
    }

    // Verificar si hay cambios de fecha y si hay asistentes
    const hasDateChanges = await checkForDateChanges();
    if (hasDateChanges) {
      // Mostrar modal de confirmación
      setPendingFormData(formData);
      setShowDateChangeModal(true);
      return;
    }

    // Si no hay cambios de fecha o no hay asistentes, proceder normalmente
    await saveEventChanges(formData);
  };

  const checkForDateChanges = async () => {
    try {
      const formData = await eventFormRef.current.getFormData();
      if (!formData || !initialData) return false;

      // Obtener fechas del formulario
      const newStartDate = formData.get('startDate');
      const newEndDate = formData.get('endDate');

      // Crear fechas completas para comparación usando timestamps
      const originalStartDate = new Date(initialData.startDate);
      const originalEndDate = new Date(initialData.endDate);

      // Construir fechas completas del formulario
      const formStartDateTime = new Date(newStartDate);
      const formEndDateTime = new Date(newEndDate);

      // Comparar usando timestamps redondeados a minutos para evitar falsos positivos
      const originalStartTime = Math.floor(originalStartDate.getTime() / 60000);
      const originalEndTime = Math.floor(originalEndDate.getTime() / 60000);
      const formStartTime = Math.floor(formStartDateTime.getTime() / 60000);
      const formEndTime = Math.floor(formEndDateTime.getTime() / 60000);

      // Verificar cambios en fecha/hora de inicio O fin
      const hasStartDateChanges = originalStartTime !== formStartTime;
      const hasEndDateChanges = originalEndTime !== formEndTime;
      const hasDateTimeChanges = hasStartDateChanges || hasEndDateChanges;

      // Solo mostrar modal si hay cambios reales Y hay asistentes
      if (hasDateTimeChanges) {
        const attendeesCount = initialData.attendees?.length || 0;
        const hasPaidTickets = initialData.attendees?.some(attendee =>
          attendee.ticketId?.type === TICKET_PAID
        ) || false;

        // Solo mostrar el modal si hay asistentes
        if (attendeesCount > 0) {
          // Formatear fecha y hora para el modal en formato ISO compatible
          const startYear = formStartDateTime.getFullYear();
          const startMonth = String(formStartDateTime.getMonth() + 1).padStart(2, '0');
          const startDay = String(formStartDateTime.getDate()).padStart(2, '0');
          const startHours = String(formStartDateTime.getHours()).padStart(2, '0');
          const startMinutes = String(formStartDateTime.getMinutes()).padStart(2, '0');

          // Formatear fecha y hora de fin
          const endYear = formEndDateTime.getFullYear();
          const endMonth = String(formEndDateTime.getMonth() + 1).padStart(2, '0');
          const endDay = String(formEndDateTime.getDate()).padStart(2, '0');
          const endHours = String(formEndDateTime.getHours()).padStart(2, '0');
          const endMinutes = String(formEndDateTime.getMinutes()).padStart(2, '0');

          setDateChangeInfo({
            newDate: `${startYear}-${startMonth}-${startDay}`,
            newTime: `${startHours}:${startMinutes}`,
            newEndDate: `${endYear}-${endMonth}-${endDay}`,
            newEndTime: `${endHours}:${endMinutes}`,
            attendeesCount,
            hasPaidTickets
          });
          return true;
        }
      }

      return false;
    } catch (error) {
      console.error('Error checking for date changes:', error);
      return false;
    }
  };

  const saveEventChanges = async (formData) => {
    try {
      // Fix for terrain format - ensure it's properly formatted before sending
      const terrainValue = formData.get('terrain');
      if (terrainValue && terrainValue.includes(',')) {
        // If it's a comma-separated string, convert to array and set back
        const terrainArray = terrainValue.split(',');
        formData.delete('terrain');
        terrainArray.forEach(item => {
          formData.append('terrain', item);
        });
      }

      // Fix for organizerVehicle - only include if it has a valid value
      if (formData.get('organizerVehicle') === 'undefined' || formData.get('organizerVehicle') === '') {
        formData.delete('organizerVehicle');
      }

      const response = await axiosClient.put(
        `/api/events/manage/${id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        toast.success(t('editEvent.success', 'Evento actualizado exitosamente.'));
        navigate(`/events/manage/${id}`);
      } else {
        console.error('Error actualizando el evento');
        toast.error(t('editEvent.error', 'Hubo un error al actualizar el evento. Inténtalo de nuevo.'));
      }
    } catch (error) {
      console.error('Error actualizando el evento:', error);
      toast.error(t('editEvent.error', 'Hubo un error al actualizar el evento. Inténtalo de nuevo.'));
    }
  };

  const handleDateChangeConfirm = async () => {
    setShowDateChangeModal(false);
    if (pendingFormData) {
      await saveEventChanges(pendingFormData);
      setPendingFormData(null);
    }
  };

  const handleDateChangeCancel = () => {
    setShowDateChangeModal(false);
    setPendingFormData(null);
  };

  return (

    <>
      <Topbar>
        <Container>
          <Heading>
            {t('editEvent.title')}
          </Heading>
          <Links>
            <Button size="l" sizeTablet="l" sizeMobile="m" $variant="outline" onClick={handleDiscard}>
              {t('createEventPage.discard')}
            </Button>
            <Button size="l" sizeTablet="l" sizeMobile="m" $variant="" onClick={handleSaveChanges}>
              {t('editEvent.saveChanges')}
            </Button>
          </Links>
        </Container>
      </Topbar>

      {initialData ? (
        <EventForm ref={eventFormRef} initialData={initialData} isEditMode={true} onSubmit={handleSaveChanges} />
      ) : (
        <Loader>
          <SpinnerCircular size={30} thickness={180} speed={100} color={theme.fill.brandMain} secondaryColor={theme.fill.defaultWeak} />
          <Typography as="p" $variant="body-2-medium" color={theme.colors.defaultWeak}>
            {t('editEvent.loading')}
          </Typography>
        </Loader>
      )}

      <DateChangeConfirmationModal
        isOpen={showDateChangeModal}
        onClose={handleDateChangeCancel}
        onConfirm={handleDateChangeConfirm}
        attendeesCount={dateChangeInfo.attendeesCount}
        hasPaidTickets={dateChangeInfo.hasPaidTickets}
        newDate={dateChangeInfo.newDate}
        newTime={dateChangeInfo.newTime}
        newEndDate={dateChangeInfo.newEndDate}
        newEndTime={dateChangeInfo.newEndTime}
      />
    </>
  );
};

export default EditEvent;

// Estilos (puedes mantener tus estilos existentes)


const Topbar = styled.header`
  align-items: center;
  background: ${({ theme }) => theme.fill.defaultSubtle};
  border-bottom: 1px solid ${({ theme }) => theme.border.defaultWeak};
  display: flex;
  flex-direction: row;
  justify-content: center;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 100;
`;

const Container = styled.nav`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  max-width: 1400px;
  padding: ${({ theme }) => theme.sizing.sm} ${({ theme }) => theme.sizing.md};
  width: 100%;
`;

const Heading = styled.h1`
  color: ${({ theme }) => theme.colors.defaultStrong};
  font-family: MonaSans;
  font-feature-settings: 'ss01' on, 'ss05' on, 'ss06' on, 'ss07' on;  font-size: 20px;
  font-style: normal;
  font-variant-numeric: lining-nums tabular-nums;
  font-weight: 600;
  line-height: 140%;
`;

const Links = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
`;

const Loader = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.sm};
  height: 100vh;
  justify-content: center;
`;
